import React, { useMemo, useState, useCallback } from 'react';
import { createUseStyles } from 'react-jss';

import {
  ModeratedTaskSetting,
  Session,
  useMarkStudyStepAsValidated,
  useReplaceTaskSettingsAvailabilities,
  useTaskSetting,
  useTaskSettingsAvailabilities,
  useUpdateModeratedTaskSetting,
  useGetCalendarEvents,
  useGetModeratorsMeetingEvents,
} from '@app/api';

import { Theme } from '@app/theme';
import {
  BaseEvent,
  Calendar,
  CalendarSettingsPanel,
  useToast,
  EventType,
} from 'components';

import { EventSourceFuncArg } from '@fullcalendar/core';
import { EditSessionStep } from './steps';

import { EditSessionLayout } from './components/EditSessionLayout';
import { SessionSubmitLine } from './components/SessionSubmitLine';
import {
  clearAvailabilitiesForGroup,
  mergeAdjacentAvailabilities,
} from './utils/mergeAdjacentAvailabilities';
import { useRedirectIfDraft } from './hooks/useRedirectIfDraft';
import { CalendarAnnoncement } from './components/CalendarAnnoncement';
import { tdmzEventToCalendarEvent } from '../../../components/Calendar/utils/tdmzEventToCalendarEvent';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    padding: [[theme.marginBase * 4, theme.marginBase * 5]],
    height: '100%',
    maxWidth: '100%',
  },
}));

interface Props {
  session: Session;
}

export const EditSessionSchedule = ({ session }: Props) => {
  const classes = useStyles();
  const [settingOpened, setSettingOpened] = useState(false);
  const toggleSetting = useCallback(
    () => setSettingOpened((opened) => !opened),
    [setSettingOpened],
  );
  const toast = useToast();
  const { isLoading: isSaving, mutateAsync: replaceAvailabilities } =
    useReplaceTaskSettingsAvailabilities(session.taskSettingId);
  const { data: initialAvailabilities } = useTaskSettingsAvailabilities(
    session.taskSettingId,
  );
  const getCalendarEvents = useGetCalendarEvents();

  const { data: initialTaskSetting } = useTaskSetting<ModeratedTaskSetting>(
    session.taskSettingId,
  );
  const { mutateAsync: updateTaskSetting, isLoading: taskSettingSaving } =
    useUpdateModeratedTaskSetting(session.taskSettingId);

  const { mutateAsync: markStepAsValidated } = useMarkStudyStepAsValidated(
    session.id,
  );
  const redirectIfDraft = useRedirectIfDraft(session);
  const [taskSettingState, setTaskSetting] =
    useState<ModeratedTaskSetting | null>(null);

  const taskSetting = taskSettingState || initialTaskSetting;

  const getModeratorsMeetingEvents = useGetModeratorsMeetingEvents(
    taskSetting?.moderatorAccountId,
    session.organizationId,
  );

  const [availabilitiesState, setAvailabilitiesState] = React.useState<
    BaseEvent[] | null
  >(null);

  const setAvailabilities = useCallback(
    (availabilities: BaseEvent[]) => {
      if (isGroup) {
        const res = mergeAdjacentAvailabilities(availabilities);
        setAvailabilitiesState(res);
      } else {
        const res = clearAvailabilitiesForGroup(availabilities);
        setAvailabilitiesState(res);
      }
    },
    [setAvailabilitiesState],
  );

  const availabilities: BaseEvent[] = useMemo(() => {
    if (availabilitiesState) {
      return availabilitiesState;
    }
    if (!initialAvailabilities) {
      return [];
    }
    return initialAvailabilities.map((a) => ({
      id: a.id,
      start: new Date(a.start),
      end: new Date(a.end),
      type: EventType.AVAILABILITY,
    }));
  }, [initialAvailabilities, availabilitiesState]);

  const isGroup = !!taskSetting?.groupMeeting;
  const onCreateEvent = React.useCallback(
    (event: BaseEvent) => {
      setAvailabilities([...availabilities, event]);
    },
    [setAvailabilities, availabilities, isGroup],
  );

  const onEditEvent = React.useCallback(
    (event: BaseEvent) => {
      const index = availabilities.findIndex((e) => e.id === event.id);
      if (index === null || index === -1 || !availabilities) {
        return;
      }
      const copy = [...availabilities];
      copy[index] = event;
      setAvailabilities(copy);
    },
    [setAvailabilities, availabilities],
  );

  const onDeleteEvent = React.useCallback(
    (event: BaseEvent) => {
      const res = availabilities.filter((e) => e.id !== event.id);
      setAvailabilities(res);
    },
    [setAvailabilities, availabilities],
  );

  const onSubmit = React.useCallback(async () => {
    try {
      const taskSettingId = session.taskSettingId;
      if (!taskSettingId) {
        return;
      }
      if (taskSettingState) {
        await updateTaskSetting(taskSettingState);
      }
      await replaceAvailabilities({
        availabilities: availabilities,
      });
      await markStepAsValidated({ step: EditSessionStep.SCHEDULE });
      redirectIfDraft();
      toast.saved();
    } catch (e) {
      toast.exception(e);
    }
  }, [
    availabilities,
    session.taskSettingId,
    replaceAvailabilities,
    toast,
    taskSettingState,
    updateTaskSetting,
  ]);

  const getCalendarEventsFunction = useCallback(
    (calendarId?: string) => async (args: EventSourceFuncArg) => {
      if (!calendarId) {
        return [];
      }
      const events = await getCalendarEvents(calendarId, {
        start: args.start,
        end: args.end,
      });

      if (!events) {
        return [];
      }

      return events.map((event) => ({
        ...event,
        type: EventType.EVENT,
        editable: false,
        resourceId: taskSetting?.moderatorCalendarId,
      }));
    },
    [getCalendarEvents, taskSetting],
  );

  const getModeratorMeetingEvent = useCallback(
    async (args: EventSourceFuncArg) => {
      const events = await getModeratorsMeetingEvents({
        start: args.start,
        end: args.end,
      });
      if (!events) {
        return [];
      }

      return events.map((event) => {
        const baseEvent = tdmzEventToCalendarEvent(event);
        const thisStudy = event.sessionId === session?.id;
        return {
          ...baseEvent,
          type: thisStudy ? EventType.APPOINTMENT : EventType.EVENT,
        };
      });
    },
    [getModeratorsMeetingEvents],
  );

  const eventSources = useMemo(() => {
    return [
      { events: availabilities },
      {
        events: getModeratorMeetingEvent,
      },
      {
        events: getCalendarEventsFunction(taskSetting?.moderatorCalendarId),
      },
    ];
  }, [
    availabilities,
    getModeratorMeetingEvent,
    taskSetting,
    getCalendarEventsFunction,
  ]);

  return (
    <EditSessionLayout
      session={session}
      footer={
        <SessionSubmitLine
          session={session}
          onSubmit={onSubmit}
          saving={isSaving || taskSettingSaving}
        />
      }
      contentClassName={classes.container}
      rightMenuOpened={settingOpened}
      extraRightMenu={
        <CalendarSettingsPanel
          onRequestClose={toggleSetting}
          onSettingChanged={setTaskSetting}
          settings={taskSetting}
        />
      }
    >
      <Calendar
        onCreateEvent={onCreateEvent}
        onEditEvent={onEditEvent}
        onDeleteEvent={onDeleteEvent}
        eventSources={eventSources}
        onSettingsClick={toggleSetting}
        settings={taskSetting}
        minDuration={session.duration}
      />
      <CalendarAnnoncement />
    </EditSessionLayout>
  );
};
